"use client";

import { useState, useRef, useEffect } from "react";
import viugoChevronDownIcon from "@/assets/images/viugo/kbv-v16-chevron-down-icon.svg";

const PRIMARY = "var(--theme-primary)";

const ODDS_TRIGGER_GRADIENT =
    "linear-gradient(45deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 80%, var(--theme-foreground)) 100%)";

const ODD_COLOR_FILTER_BOOKMAKER_NAME = "var(--theme-foreground)";
const ODD_BG_COLOR_FILTER_BOOKMAKER_NAME = "var(--theme-primary)";
const ODD_BORDER_FILTER_BOOKMAKER_RADIUS = 8;
const ITEM_BOOKMAKER_CHILD_BORDER = "var(--theme-btn-border)";

function ChevronIcon({ open, className = "" }) {
    return (
        <img
            src={viugoChevronDownIcon}
            alt=""
            width={20}
            height={20}
            className={`relative z-10 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""} ${className}`}
            aria-hidden
        />
    );
}

export default function CumeoOddsAndBookmaker({
    extendValue,
    bookmakerValue,
    extendOptions,
    bookmakerOptions,
    onExtendChange,
    onBookmakerChange,
    extendPlaceholder = "Tỷ lệ HONGKONG",
    bookmakerPlaceholder = "Tất cả",
}) {
    const [oddsOpen, setOddsOpen] = useState(false);
    const [bookmakerOpen, setBookmakerOpen] = useState(false);
    const oddsRef = useRef(null);
    const bookRef = useRef(null);

    const selectedExtend = extendOptions.find(
        (o) => String(o.value) === String(extendValue),
    );
    const selectedBookmaker = bookmakerOptions.find(
        (o) => String(o.value) === String(bookmakerValue),
    );

    useEffect(() => {
        const close = (e) => {
            const t = e.target;
            if (
                oddsRef.current?.contains(t) ||
                bookRef.current?.contains(t)
            ) {
                return;
            }
            setOddsOpen(false);
            setBookmakerOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const openOdds = () => {
        setBookmakerOpen(false);
        setOddsOpen((v) => !v);
    };

    const openBook = () => {
        setOddsOpen(false);
        setBookmakerOpen((v) => !v);
    };

    return (
        <div className="flex w-full min-w-0 shrink-0 flex-col gap-2 md:w-auto md:flex-row md:items-stretch">
            <div
                className="relative min-w-0 w-full shrink-0 md:w-[160px]"
                ref={oddsRef}
            >
                <button
                    type="button"
                    onClick={openOdds}
                    className={`group relative z-0 flex w-full min-w-0 cursor-pointer select-none items-center gap-1.5 overflow-hidden truncate rounded-lg border border-solid border-[var(--theme-primary)] bg-[var(--theme-foreground)] bg-no-repeat px-[10px] py-[3px] text-left text-[13px] font-medium [background-position:bottom_left] [background-size:0%_0%] min-h-[38px] hover:[background-size:250%_250%] hover:text-white ${oddsOpen ? "[background-size:250%_250%] text-white" : "text-[var(--theme-primary)]"}`}
                    style={{
                        backgroundColor: "var(--theme-foreground)",
                        backgroundImage: ODDS_TRIGGER_GRADIENT,
                        backgroundPosition: "bottom left",
                        backgroundRepeat: "no-repeat",
                        transition:
                            "background-size 0.6s cubic-bezier(0.4, 0, 0.2, 1), color 0.4s ease",
                    }}
                >
                    <span className="relative z-10 min-w-0 flex-1 truncate transition-colors duration-300 group-hover:text-white">
                        {selectedExtend
                            ? selectedExtend.label
                            : extendPlaceholder}
                    </span>
                    <ChevronIcon
                        open={oddsOpen}
                        className={
                            oddsOpen
                                ? "brightness-0 invert"
                                : "transition duration-300 group-hover:brightness-0 group-hover:invert"
                        }
                    />
                </button>

                {oddsOpen && (
                    <ul
                        className="absolute left-0 right-0 top-full z-[999] m-0 mt-1 !flex w-full min-w-full cursor-pointer list-none !flex-col !flex-nowrap overflow-hidden bg-[var(--theme-text)] p-0 shadow-lg"
                        style={{
                            border: `1px solid ${PRIMARY}`,
                            borderRadius: 4,
                        }}
                    >
                        {extendOptions.map((item) => {
                            const active =
                                String(item.value) === String(extendValue);
                            return (
                                <li
                                    key={item.value}
                                    role="option"
                                    aria-selected={active}
                                    className={`m-0 !block !w-full !float-none px-3 py-2 text-left text-[12px] text-[var(--theme-primary)] transition-colors ${
                                        active
                                            ? "bg-[var(--theme-primary)] text-white"
                                            : "bg-[var(--theme-text)] hover:bg-[var(--theme-highlight)] hover:text-white"
                                    }`}
                                    onClick={() => {
                                        onExtendChange(item.value);
                                        setOddsOpen(false);
                                    }}
                                >
                                    {item.label}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div
                className="relative min-w-0 w-full shrink-0 md:w-[100px]"
                ref={bookRef}
            >
                <button
                    type="button"
                    onClick={openBook}
                    className="flex w-full min-w-0 cursor-pointer select-none items-center gap-1.5 truncate rounded-lg border border-solid px-[10px] py-[3px] text-left text-[13px] font-medium text-white min-h-[38px] transition-opacity hover:opacity-95"
                    style={{
                        backgroundColor: PRIMARY,
                        borderColor: PRIMARY,
                    }}
                >
                    <span className="min-w-0 flex-1 truncate">
                        {selectedBookmaker
                            ? selectedBookmaker.label
                            : bookmakerPlaceholder}
                    </span>
                    <ChevronIcon open={bookmakerOpen} className="brightness-0 invert" />
                </button>

                {bookmakerOpen && (
                    <div
                        className="absolute right-0 top-full z-[999] m-0 mt-1 w-[min(100vw-1rem,300px)] cursor-default bg-[var(--theme-foreground)] p-0 shadow-lg"
                        style={{
                            border: `1px solid ${PRIMARY}`,
                            borderRadius: 8,
                        }}
                    >
                        <div className="border-b border-[var(--theme-btn-border)] px-3 py-2.5 text-center text-[15px] font-bold uppercase leading-tight text-[var(--theme-text)]">
                            CHỌN KÈO NHÀ CÁI
                        </div>
                        <div className="grid grid-cols-2 gap-[15px] border-t border-[var(--theme-btn-border)] p-[10px]">
                            {bookmakerOptions.map((item) => {
                                const active =
                                    String(item.value) ===
                                    String(bookmakerValue);
                                return (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className="box-border flex min-h-0 w-full min-w-0 items-center justify-center border border-solid py-[5px] text-center text-[12px] font-medium leading-snug transition-[background-color,color,border-color] duration-200"
                                        style={{
                                            borderRadius: ODD_BORDER_FILTER_BOOKMAKER_RADIUS,
                                            ...(active
                                                ? {
                                                      backgroundColor:
                                                          ODD_BG_COLOR_FILTER_BOOKMAKER_NAME,
                                                      color: ODD_COLOR_FILTER_BOOKMAKER_NAME,
                                                      border: `1px solid ${ODD_BG_COLOR_FILTER_BOOKMAKER_NAME}`,
                                                  }
                                                : {
                                                      backgroundColor:
                                                          ODD_COLOR_FILTER_BOOKMAKER_NAME,
                                                      color: ODD_BG_COLOR_FILTER_BOOKMAKER_NAME,
                                                      border: `1px solid ${ITEM_BOOKMAKER_CHILD_BORDER}`,
                                                  }),
                                        }}
                                        onMouseEnter={(e) => {
                                            if (active) return;
                                            e.currentTarget.style.backgroundColor =
                                                ODD_BG_COLOR_FILTER_BOOKMAKER_NAME;
                                            e.currentTarget.style.color =
                                                ODD_COLOR_FILTER_BOOKMAKER_NAME;
                                            e.currentTarget.style.border = `1px solid ${ODD_BG_COLOR_FILTER_BOOKMAKER_NAME}`;
                                        }}
                                        onMouseLeave={(e) => {
                                            if (active) return;
                                            e.currentTarget.style.backgroundColor =
                                                ODD_COLOR_FILTER_BOOKMAKER_NAME;
                                            e.currentTarget.style.color =
                                                ODD_BG_COLOR_FILTER_BOOKMAKER_NAME;
                                            e.currentTarget.style.border = `1px solid ${ITEM_BOOKMAKER_CHILD_BORDER}`;
                                        }}
                                        onClick={() => {
                                            onBookmakerChange(item.value);
                                            setBookmakerOpen(false);
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

